import { Suspense, useRef, useEffect, useMemo, useState } from "react";
import {
  Canvas,
  useFrame,
  useThree,
  useLoader,
  extend,
} from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "three-stdlib";
import { FontLoader } from "three-stdlib";
import { TextGeometry } from "three-stdlib";
import { useAuth } from "@/Providers/Auth";
import { useInfluencer } from "@/Providers/Influencer";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

// Extend OrbitControls for JSX usage
extend({ OrbitControls });

function Controls() {
  const { camera, gl } = useThree();
  const controlsRef = useRef();

  useEffect(() => {
    if (!controlsRef.current) {
      controlsRef.current = new OrbitControls(camera, gl.domElement);
      controlsRef.current.enableDamping = true;
      controlsRef.current.dampingFactor = 0.05;
      controlsRef.current.minDistance = 3;
      controlsRef.current.maxDistance = 8;
      controlsRef.current.minPolarAngle = Math.PI / 6;
      controlsRef.current.maxPolarAngle = Math.PI - Math.PI / 6;
    }

    return () => {
      if (controlsRef.current) controlsRef.current.dispose();
    };
  }, [camera, gl]);

  useFrame(() => {
    if (controlsRef.current) controlsRef.current.update();
  });

  return controlsRef.current ? (
    <primitive object={controlsRef.current} />
  ) : null;
}

export default function Card3DViewer() {
  return (
    <div className="w-full h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      <Canvas shadows dpr={[1, 2]} camera={{ position: [0, 0, 5], fov: 35 }}>
        <Suspense fallback={null}>
          <ambientLight intensity={0.5} />
          <spotLight
            position={[10, 10, 10]}
            angle={0.15}
            penumbra={1}
            intensity={1}
            castShadow
          />
          <directionalLight position={[-5, 5, 5]} intensity={0.5} />
          <Card3D />
          <Controls />
        </Suspense>
      </Canvas>

      <div className="absolute bottom-4 left-0 right-0 text-center text-white text-sm bg-black/50 py-2 px-4 mx-auto w-fit rounded-full">
        Click and drag to rotate • Scroll to zoom
      </div>
    </div>
  );
}

function Card3D() {
  const width = 1.6;
  const height = 3.2;
  const depth = 0.08;

  const influencerState = useInfluencer();
  const authstate = useAuth();

  const [TempInfluData, setTempInfluData] = useState<{
    avgLikes: number;
    avgComments: number;
    avgEngagement: number;
    avgEngagementPercent: any;
    avgReposts: number;
    followers: number;
    topThreeEngagedPosts: [];
    name: string;
    gender: string;
    tagline: string;
    location: string;
    profile_image: string;
    experience: string;
    badge: string;
    featured: [];
    profile_url: string;
    firstThirtyLikes: number[];
    firstThirtyComments: number[];
  }>();

  const getTempInfluencerData = async () => {
    await influencerState
      .getTempInfluencerLinkedinProfileFromSlug(authstate?.loggedUser?.slug)
      .then((resData) => {
        if (resData.success) {
          setTempInfluData(resData?.LinkedinAnalysis);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    getTempInfluencerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authstate]);

  const frontGradientFrom = "#D10000";
  const frontGradientTo = "#000000";
  const profileImageUrl =
    TempInfluData?.profile_image || authstate?.loggedUser?.profile
      ? `${host}/api/file/proxyImage?imageUrl=${encodeURIComponent(
          TempInfluData?.profile_image || authstate.loggedUser.profile
        )}`
      : "";
  const logoImageUrl = authstate?.loggedUser?.profile
    ? `${host}/api/file/proxyImage?imageUrl=${encodeURIComponent(
        "https://anchors-assets.s3.ap-south-1.amazonaws.com/black+bg+logo+anchors.jpg"
      )}`
    : "";

  return (
    <group position={[0, 0, 0]} rotation={[0, -0.15, 0]}>
      {/* Card Base */}
      <mesh castShadow receiveShadow>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial color="#0a0a0a" metalness={0.8} roughness={0.1} />
      </mesh>

      {/* FRONT CARD */}
      <group position={[0, 0, depth / 2 + 0.001]}>
        {/* Gradient Background */}
        <mesh>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial
            map={generateGradientTexture(frontGradientFrom, frontGradientTo)}
          />
        </mesh>

        {/* Circular Image */}
        <CircularImage
          position={[0, height / 2 - height * 0.28, 0.001]}
          radius={(100 / 320) * width}
          imageUrl={profileImageUrl}
        />

        {/* Wrapped Text - Bold Name */}
        <TextComponent
          position={[0, height / 2.2 - height * 0.45, 0.001]}
          text={splitText(authstate?.loggedUser?.name, 3)}
          size={0.1}
          bold
        />

        {/* Wrapped Text - Message (Separate lines) */}
        <WrappedText
          position={[0, height / 2.2 - height * 0.65, 0.001]}
          lines={
            authstate?.loggedUser?.gender?.toLowerCase() === "female"
              ? [
                  "Did You Know?",
                  "42% of social media users in India",
                  "are women. Your voice is powerful",
                  "and influential online!",
                ]
              : [
                  "This Women’s Day,",
                  "I'm celebrating the incredible achievements",
                  "of women around the globe.",
                ]
          }
          size={0.05}
        />

        {/* Women's Day Message */}
        <TextComponent
          position={[0, height / 1.25 - height * 0.9, 0.001]}
          text={[
            `${
              authstate?.loggedUser?.gender?.toLowerCase() === "female"
                ? "Wish you a Happy Women's Day!"
                : "Celebrating Women’s  Day Together!"
            }`,
          ]}
          size={0.05}
        />

        {/* Followers with LinkedIn Icon */}
        <FollowersComponent
          followers={TempInfluData?.followers}
          position={[0, height / 1.05 - height * 1.1, 0.001]}
        />
      </group>

      {/* BACK CARD - Mirror of front */}
      <group position={[0, 0, -depth / 2 - 0.001]} rotation={[0, Math.PI, 0]}>
        {/* Gradient Background */}
        <mesh>
          <planeGeometry args={[width, height]} />
          <meshBasicMaterial
            map={generateGradientTexture(frontGradientFrom, frontGradientTo)}
          />
        </mesh>

        {/* Circular Image */}
        <CircularImage
          position={[0, height / 2 - height * 0.28, 0.001]}
          radius={(100 / 520) * width}
          imageUrl={logoImageUrl}
        />

        {/* Wrapped Text - Bold Name */}
        <TextComponent
          position={[0, height / 2.2 - height * 0.45, 0.001]}
          text={["anchors"]}
          size={0.1}
          bold
        />

        {/* Wrapped Text - Message (Separate lines) */}
        <WrappedText
          position={[0, height / 2 - height * 0.65, 0.001]}
          lines={[
            "Did You Know?",
            "42% of social media users in India",
            "are women. Your voice is powerful",
            "and influential online!",
          ]}
          size={0.05}
        />

        {/* Women's Day Message */}
        <TextComponent
          position={[0, height / 1.15 - height * 0.9, 0.001]}
          text={["Wish you a Happy Women's Day!"]}
          size={0.05}
        />

        {/* Followers with LinkedIn Icon */}
        {/* <FollowersComponent position={[0, height / 1 - height * 1.1, 0.001]} /> */}
      </group>
    </group>
  );
}

function CircularImage({ position, radius, imageUrl }) {
  const texture = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const placeholderTexture = new THREE.TextureLoader().load(
      "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/image-5SnI3WYUnE2TZdj9j2rWM07xKzE0Db.png"
    );

    if (!imageUrl) return placeholderTexture;

    const tex = loader.load(imageUrl, (loadedTexture) => {
      loadedTexture.needsUpdate = true;
      loadedTexture.minFilter = THREE.LinearFilter;
      loadedTexture.magFilter = THREE.LinearFilter;
    });

    return tex;
  }, [imageUrl]);

  return (
    <mesh position={position}>
      <circleGeometry args={[radius, 64]} />
      <meshBasicMaterial map={texture} side={THREE.DoubleSide} />
    </mesh>
  );
}

// Component for Wrapped Text (Multiple Lines)
function WrappedText({ position, lines, size }) {
  return (
    <group position={position}>
      {lines.map((line, i) => (
        <TextComponent
          key={i}
          position={[0, -i * size * 1.5, 0]}
          text={[line]}
          size={size}
        />
      ))}
    </group>
  );
}
function TextComponent({ position, text, size, bold }) {
  const font = useLoader(
    FontLoader,
    bold
      ? "https://threejs.org/examples/fonts/helvetiker_bold.typeface.json"
      : "https://threejs.org/examples/fonts/helvetiker_regular.typeface.json"
  );

  const textGeometries = useMemo(() => {
    if (!font) return [];
    return text.map((line) => {
      const geometry = new TextGeometry(line, {
        font,
        size: size || 0.1,
        height: 0.01,
        curveSegments: 12,
        bevelEnabled: false,
      });

      // Center text horizontally
      geometry.computeBoundingBox();
      const textWidth = geometry.boundingBox.max.x - geometry.boundingBox.min.x;
      geometry.translate(-textWidth / 2, 0, 0);

      return geometry;
    });
  }, [font, text, size]);

  // Dispose of old geometries on unmount
  useEffect(() => {
    return () => {
      textGeometries.forEach((geometry) => geometry.dispose());
    };
  }, [textGeometries]);

  return (
    <group position={position}>
      {textGeometries.map((geometry, i) => (
        <mesh key={i} position={[0, -i * size * 1.2, 0]}>
          <primitive object={geometry} />
          <meshStandardMaterial color="white" />
        </mesh>
      ))}
    </group>
  );
}

// Followers Component with LinkedIn Icon
function FollowersComponent({ position, followers }) {
  const authState = useAuth();
  return (
    <group position={position}>
      {/* LinkedIn Icon */}
      {/* <mesh position={[-0.3, 0, 0]}>
        <boxGeometry args={[0.1, 0.1, 0.01]} />
        <meshStandardMaterial color="white" />
      </mesh> */}

      {/* Followers Text */}
      <TextComponent
        position={[0, 0, 0]}
        text={[
          `${
            followers ||
            parseInt(
              authState?.loggedUser?.linkedinfollowers
            )?.toLocaleString() ||
            0
          } followers`,
        ]}
        size={0.06}
      />
    </group>
  );
}

function generateGradientTexture(from, to) {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const context = canvas.getContext("2d");

  if (context) {
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, from);
    gradient.addColorStop(1, to);
    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

function splitText(text, wordsPerLine) {
  if (!text) return [""]; // Default fallback
  const words = text.split(" ");
  const lines = [];

  for (let i = 0; i < words.length; i += wordsPerLine) {
    lines.push(words.slice(i, i + wordsPerLine).join(" "));
  }
  console.log(lines);
  return lines;
}
